import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './CitationChip.css';

const OPEN_EVENT = 'term-tooltip-open';

const TYPE_CONFIG = {
  news_article: { label: 'News' },
  tweet:        { label: 'Tweet' },
  polymarket:   { label: 'Prediction' },
  fred_series:  { label: 'FRED' },
  job_signal:   { label: 'Jobs' },
  bdc_signal:   { label: 'BDC' },
  analyst_note: { label: 'Analyst' },
  market_price: { label: 'Price' },
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function CitationChip({ num, source, paperArticle }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const idRef = useRef(Math.random());
  const close = useCallback(() => setOpen(false), []);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) { close(); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    const w = 300, h = paperArticle ? 220 : 160;
    const left = Math.min(rect.left, window.innerWidth - w - 16);
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < h + 16
      ? rect.top + window.scrollY - h - 6
      : rect.bottom + 6 + window.scrollY;
    setPos({ top, left });
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { id: idRef.current } }));
    setOpen(true);
  }, [open, close, paperArticle]);

  useEffect(() => {
    const onOther = (e) => { if (e.detail.id !== idRef.current) close(); };
    document.addEventListener(OPEN_EVENT, onOther);
    return () => document.removeEventListener(OPEN_EVENT, onOther);
  }, [close]);

  useEffect(() => {
    if (!open) return;
    const onOut = () => close();
    document.addEventListener('click', onOut);
    return () => document.removeEventListener('click', onOut);
  }, [open, close]);

  const cfg = TYPE_CONFIG[source?.source_type] || TYPE_CONFIG.news_article;
  const date = formatDate(source?.metadata?.published_at) || formatDate(source?.retrieved_at);
  const isNews = source?.source_type === 'news_article';
  const polyPct = source?.source_type === 'polymarket' && source?.metadata?.market_price != null
    ? Math.round(source.metadata.market_price * 100) : null;

  // Summary to show: paper article summary (truncated) or raw quote
  const summary = paperArticle?.summary || source?.quote || '';

  return (
    <>
      <span
        ref={triggerRef}
        className={`cite-chip${open ? ' cite-chip--active' : ''}`}
        onClick={handleClick}
      >
        [{num}]
      </span>
      {open && source && createPortal(
        <div
          className="cite-popup"
          style={{ top: pos.top, left: pos.left }}
          onClick={e => e.stopPropagation()}
        >
          <div className="cite-popup__header">
            <span className="cite-popup__type">{cfg.label}</span>
            {date && <span className="cite-popup__date">{date}</span>}
          </div>

          {/* News article with summary */}
          {isNews && paperArticle ? (
            <>
              <div className="cite-popup__summary">{summary}</div>
              {source.url && (
                <div className="cite-popup__footer">
                  <a
                    className="cite-popup__paper-link"
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {source.source_name
                      ? source.source_name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                      : 'Read the full article'} →
                  </a>
                </div>
              )}
            </>
          ) : (
            <>
              {source.url
                ? <a className="cite-popup__title" href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a>
                : <div className="cite-popup__title">{source.title}</div>
              }
              {source.source_name && <div className="cite-popup__source">{source.source_name}</div>}
              {polyPct !== null && (
                <div className="cite-popup__poly">
                  <div className="cite-popup__poly-bar">
                    <div className="cite-popup__poly-fill" style={{ width: `${polyPct}%` }} />
                  </div>
                  <span className="cite-popup__poly-pct">{polyPct}% probability</span>
                </div>
              )}
              {summary && source.source_type !== 'polymarket' && (
                <div className="cite-popup__quote">{summary}</div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
